/**
 * useCurrencyFormatter Hook
 * Provides currency formatting based on partner currency settings
 */

import { useCallback } from 'react';

import { selectUserCurrency } from '../store/slices';
import { SupportedCurrency } from '../store/types';
import { useAppSelector } from './use-app-selector';

interface CurrencyConfig {
    symbol: string;
    position: 'prefix' | 'suffix';
}

const currencyConfig: Record<SupportedCurrency, CurrencyConfig> = {
    [SupportedCurrency.USD]: { symbol: '$', position: 'prefix' },
    [SupportedCurrency.CAD]: { symbol: 'CA$', position: 'prefix' },
    [SupportedCurrency.GBP]: { symbol: '£', position: 'prefix' },
    [SupportedCurrency.EUR]: { symbol: '€', position: 'suffix' },
    [SupportedCurrency.CHF]: { symbol: 'CHF ', position: 'prefix' },
};

export interface UseCurrencyFormatterReturn {
    formatCurrency: (amount: number, decimals?: number) => string;
    currency: SupportedCurrency;
    currencySymbol: string;
}

export const useCurrencyFormatter = (): UseCurrencyFormatterReturn => {
    const currency = useAppSelector(selectUserCurrency);

    const formatCurrency = useCallback(
        (amount: number, decimals: number = 2): string => {
            const config = currencyConfig[currency] || currencyConfig[SupportedCurrency.USD];

            // -1 means no decimal formatting
            const formattedAmount = decimals === -1 ? amount.toString() : amount.toFixed(decimals);

            return config.position === 'prefix'
                ? `${config.symbol}${formattedAmount}`
                : `${formattedAmount}${config.symbol}`;
        },
        [currency],
    );

    const config = currencyConfig[currency] || currencyConfig[SupportedCurrency.USD];

    return {
        formatCurrency,
        currency,
        currencySymbol: config.symbol,
    };
};

export default useCurrencyFormatter;
